import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';

import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { getQuoteBuilderURL, getLogoUrl, formatTimestamp, getProposalURL } from '../../../helpers/common';
import env from '../../../env';
import { useHistory } from 'react-router';

const DATA = gql`
  query Data($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      CustomerLogo
      CompanyName
      Description
      WorkflowStep
      AcceptedQuote
      Token
      CurrentVersionId
      RedirectToCurrentVersion
      Status
      ProposalModified
      SignedDate
      QuoteAccessLogs {
        AccessLogId
        IP
        GeoIPCity
        GeoIPState
        GeoIPCountry
        AccessTag
        Timestamp
      }
      QuoteVersions {
        VersionId
        Token
        File
        Timestamp
      }
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;

const GENERATE_PROPOSAL = gql`
  mutation generateProposal($QuoteId: Int, $Token: String) {
    generateProposal(QuoteId: $QuoteId, Token: $Token) {
      message
      QuoteVersion {
        QuoteId
        VersionId
        Timestamp
        Token
        File
      }
    }
  }
`;

const QuoteBuilderSendProposal = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const history = useHistory();
  const proposalURLEl = useRef(null);

  // initialize states
  const [proposalURL, setProposalURL] = useState('');
  const [previous, setPrevious] = useState(false);
  const [copiedToClipboardMsg, setCopiedToClipboardMsg] = useState(false);
  const [accessLogs, setAccessLogs] = useState([]);
  const [quoteVersions, setQuoteVersions] = useState([]);
  const [disableQuote, setDisableQuote] = useState(false);
  const [generating, setGenerating] = useState(false);
  const generationTriggered = useRef(false);
  const [specifyCurrentVersion, setSpecifyCurrentVersion] = useState(false);
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [redirectToCurrentVersion, setRedirectToCurrentVersion] = useState(false);

  // initialize apollo graphql queries and mutations
  const { data, loading } = useQuery(DATA, {
    variables: { QuoteId: props.quoteId },
  });
  const [updateQuote, { data: updatedQuoteData }] = useMutation(UPDATE_QUOTE);
  const [generateProposal, { data: generatedProposalData }] = useMutation(GENERATE_PROPOSAL);

  useEffect(() => {
    if (data && data.Quote) {
      // disable screen when status is accepted, declined or voided
      const status = data.Quote.Status;
      setDisableQuote(status === 'accepted');

      if (!['SEND_PROPOSAL', 'CUSTOMER_ACCEPTED'].includes(data.Quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(data.Quote.WorkflowStep, data.Quote.QuoteId));
        return;
      }

      const versions = Array.from(data.Quote.QuoteVersions ?? []);

      const savedCurrentVersionId = data.Quote.CurrentVersionId;
      const specify = savedCurrentVersionId != null;
      setSpecifyCurrentVersion(specify);
      setCurrentVersionId(savedCurrentVersionId);
      setRedirectToCurrentVersion(!!data.Quote.RedirectToCurrentVersion);

      setProposalURL(getProposalURL(data.Quote, false));
      setAccessLogs(Array.from(data.Quote.QuoteAccessLogs));
      setQuoteVersions(versions);
    }
  }, [data, history, updateQuote]);

  useEffect(() => {
    if (data && data.Quote && data.Quote.ProposalModified && !generationTriggered.current) {
      generationTriggered.current = true;
      setGenerating(true);
      generateProposal({
        variables: {
          QuoteId: parseInt(data.Quote.QuoteId, 10),
          Token: data.Quote.Token,
        },
        refetchQueries: ['getCurrentQuote'],
      });
    }
  }, [data, generateProposal]);

  const currentVersionIdRef = useRef(data?.Quote?.CurrentVersionId);
  currentVersionIdRef.current = data?.Quote?.CurrentVersionId;

  useEffect(() => {
    if (generatedProposalData && generatedProposalData.generateProposal) {
      const version = generatedProposalData.generateProposal.QuoteVersion;
      if (version && version.Token) {
        if (!currentVersionIdRef.current) setProposalURL(`${env.PROPOSAL_BASE_URL}${version.Token}`);
        setQuoteVersions(prev => [version, ...prev]);
      }
      setGenerating(false);
    }
  }, [generatedProposalData]);

  useEffect(() => {
    if (previous === true && updatedQuoteData && updatedQuoteData.updateQuote) {
      // redirect to the new workflow step
      switch (updatedQuoteData.updateQuote.WorkflowStep) {
        case 'QUOTE_SETUP':
          history.replace(`/quote-setup/${data.Quote.QuoteId}`);
          break;
        case 'QUOTE_ITEMS':
          history.replace(`/quote-items/${data.Quote.QuoteId}`);
          break;
        case 'QUOTE_RATES':
          history.replace(`/quote-rates/${data.Quote.QuoteId}`);
          break;
        case 'CUSTOMISE_PROPOSAL':
          history.replace(`/customise-proposal/${data.Quote.QuoteId}`);
          break;
        default:
          break;
      }
      setPrevious(false);
    }
  }, [previous, updatedQuoteData, data, history]);

  const saveVersionSettings = (cvId, redirect) => {
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          CurrentVersionId: cvId,
          RedirectToCurrentVersion: redirect,
        },
      },
    });
  };

  const handleSpecifyCurrentVersion = (checked) => {
    setSpecifyCurrentVersion(checked);
    if (checked) {
      const defaultId = quoteVersions[0]?.VersionId ?? null;
      setCurrentVersionId(defaultId);
      saveVersionSettings(defaultId, redirectToCurrentVersion);
    } else {
      setCurrentVersionId(null);
      setProposalURL(`${env.PROPOSAL_BASE_URL}${quoteVersions[0]?.Token ?? data.Quote.Token}`);
      saveVersionSettings(null, redirectToCurrentVersion);
    }
  };

  const handleVersionSelect = (versionId) => {
    setCurrentVersionId(versionId);
    const version = quoteVersions.find(v => v.VersionId === versionId);
    if (version) setProposalURL(`${env.PROPOSAL_BASE_URL}${version.Token}`);
    saveVersionSettings(versionId, redirectToCurrentVersion);
  };

  const handleRedirectToggle = (checked) => {
    setRedirectToCurrentVersion(checked);
    saveVersionSettings(currentVersionId, checked);
  };

  const previewProposal = (e) => {
    e.preventDefault();
    window.open(`${proposalURL}?preview`, '_blank');
  };

  const downloadSignedProposal = (e) => {
    e.preventDefault();
    const signedProposalURL = proposalURL.replace(
      '/proposal/',
      '/proposal-pdf/'
    );
    window.open(signedProposalURL, '_blank');
  };

  const highlightText = (e) => {
    e.preventDefault();
    var proposalURLText = proposalURLEl.current;
    proposalURLText.select();
    proposalURLText.setSelectionRange(0, 99999);
  };

  const copyURLToClipboard = () => {
    var copyText = proposalURLEl.current;
    copyText.select();
    copyText.setSelectionRange(0, 99999);

    document.execCommand('copy');
    if (navigator && navigator.clipboard)
      navigator.clipboard.writeText(copyText.value);
    setCopiedToClipboardMsg(true);
  };

  const goToPreviousStep = (e, workflowStep) => {
    setPrevious(true);
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: workflowStep,
        },
      },
    });
  };

  return (
    <React.Fragment>
      {disableQuote && <div class="overlay"></div>}
      {copiedToClipboardMsg && (
        <SweetAlert
          success
          timeout={900}
          showConfirm={false}
          title="Success"
          onConfirm={(e) => setCopiedToClipboardMsg(false)}
        >
          Copied URL to clipboard!
        </SweetAlert>
      )}
      {data && data.Quote && !loading && !generating && (
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row className="align-items-center">
                <Col sm="6">
                  <h4 className="page-title">{data.Quote.Description} - Send Proposal</h4>
                </Col>
              </Row>
            </div>

            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="text-center">
                    <Row className="my-4">
                      <Col lg={{ size: 8, offset: 2 }}>
                        <Row>
                          <Col sm={12} className="px-1">
                            <Row
                              style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <Col sm={3}>
                                <i
                                  style={{ fontSize: '140px' }}
                                  className="fas fa-file-contract mr-4"
                                ></i>
                              </Col>
                              <Col sm={2} style={{ maxWidth: '100px' }}>
                                <i
                                  style={{ fontSize: '50px' }}
                                  className="fas fa-arrow-circle-right mr-4"
                                ></i>
                              </Col>
                              <Col sm={3}>
                                {data.Quote.CustomerLogo ? (
                                  <img
                                    className="img"
                                    src={getLogoUrl(data.Quote.CustomerLogo)}
                                    style={{
                                      maxHeight: '150px',
                                      width: 'auto',
                                    }}
                                    alt="Customer Logo"
                                  />
                                ) : (
                                  <h4>{data.Quote.CompanyName}</h4>
                                )}
                              </Col>
                            </Row>
                            <h5 style={{ fontSize: '18px' }} className="my-4">
                              Your Proposal is Ready to Send!
                            </h5>
                            <input
                              type="text"
                              ref={proposalURLEl}
                              className="form-control my-2"
                              value={proposalURL}
                              style={{ fontSize: '16px', padding: '24px' }}
                              onClick={(e) => highlightText(e)}
                              readOnly
                            />
                            <button
                              className="btn btn-info btn-lg btn-block my-1"
                              onClick={previewProposal}
                            >
                              Preview Proposal
                            </button>
                            {data.Quote.SignedDate !== null && (
                              <button
                                className="btn btn-dark btn-lg btn-block my-1"
                                onClick={downloadSignedProposal}
                              >
                                Download Service Agreement
                              </button>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col className="px-1">
                            <button
                              className="btn btn-success btn-lg btn-block my-1"
                              onClick={copyURLToClipboard}
                            >
                              Copy URL to Clipboard
                            </button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {!disableQuote && (
                      <Row className="my-4">
                        <Col lg={{ size: 4, offset: 4 }}>
                          <h5 style={{ fontSize: '18px' }}>
                            Or if you need to make changes...
                          </h5>
                          <ListGroup flush className="text-left">
                            <ListGroupItem
                              tag="a"
                              href={`/quote-setup/${data.Quote.QuoteId}`}
                              onClick={(e) =>
                                goToPreviousStep(e, 'QUOTE_SETUP')
                              }
                            >
                              Change Quote Settings
                            </ListGroupItem>
                            <ListGroupItem
                              tag="a"
                              href={`/quote-items/${data.Quote.QuoteId}`}
                              onClick={(e) =>
                                goToPreviousStep(e, 'QUOTE_ITEMS')
                              }
                            >
                              Edit Quote Items
                            </ListGroupItem>
                            <ListGroupItem
                              tag="a"
                              href={`/quote-rates/${data.Quote.QuoteId}`}
                              onClick={(e) =>
                                goToPreviousStep(e, 'QUOTE_RATES')
                              }
                            >
                              Edit Usage Rates
                            </ListGroupItem>
                            <ListGroupItem
                              tag="a"
                              href={`/customise-proposal/${data.Quote.QuoteId}`}
                              onClick={(e) =>
                                goToPreviousStep(e, 'CUSTOMISE_PROPOSAL')
                              }
                            >
                              Customise Proposal
                            </ListGroupItem>
                          </ListGroup>
                        </Col>
                      </Row>
                    )}

                    <Row className="text-left my-5">
                      <Col lg={{ size: 8, offset: 2 }}>
                        <h6>Proposal Versions</h6>
                        <div className="mb-2">
                          <div className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              id="specify-current-version"
                              checked={specifyCurrentVersion}
                              onChange={(e) => handleSpecifyCurrentVersion(e.target.checked)}
                            />
                            <label htmlFor="specify-current-version" className="ml-2 mb-0">Specify current version</label>
                          </div>
                          <div className="d-flex align-items-center mt-1">
                            <input
                              type="checkbox"
                              id="redirect-to-current-version"
                              checked={redirectToCurrentVersion}
                              onChange={(e) => handleRedirectToggle(e.target.checked)}
                            />
                            <label htmlFor="redirect-to-current-version" className="ml-2 mb-0">Redirect to current version</label>
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-striped table-hover">
                            <thead className="thead-dark">
                              <tr>
                                <th style={{ width: '1%' }}></th>
                                <th>Date</th>
                                <th>Token</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {quoteVersions.length > 0 &&
                                quoteVersions.map((quoteVersion, index) => {
                                  const isCurrent = specifyCurrentVersion
                                    ? quoteVersion.VersionId === currentVersionId
                                    : index === 0;
                                  return (
                                    <tr key={index}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={isCurrent}
                                          disabled={!specifyCurrentVersion}
                                          onChange={() => handleVersionSelect(quoteVersion.VersionId)}
                                        />
                                      </td>
                                      <td>{formatTimestamp(quoteVersion.Timestamp)}</td>
                                      <td>{quoteVersion.Token}</td>
                                      <td align="right">
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => window.open(`${env.PROPOSAL_BASE_URL}${quoteVersion.Token}?preview`)}
                                        >
                                          View
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              {quoteVersions.length === 0 && (
                                <tr key={0}>
                                  <td colSpan={4} className="text-center">
                                    No proposal version yet
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Col>
                    </Row>

                    <Row className="text-left my-5">
                      <Col lg={{ size: 8, offset: 2 }}>
                        <h6>Proposal Access History</h6>
                        <div className="table-responsive">
                          <table className="table table-striped table-hover">
                            <thead className="thead-dark">
                              <tr>
                                <th>Timestamp</th>
                                <th>IP Address</th>
                                <th>Location</th>
                                <th>Tag</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accessLogs.length > 0 &&
                                accessLogs.sort((a, b) =>
                                  parseInt(a.AccessLogId) <
                                  parseInt(b.AccessLogId)
                                    ? 1
                                    : -1
                                ) &&
                                accessLogs.map((accessLog, index) => (
                                  <tr key={index}>
                                    <td>{formatTimestamp(accessLog.Timestamp)}</td>
                                    <td>{accessLog.IP}</td>
                                    <td>{`${accessLog.GeoIPCity}, ${accessLog.GeoIPState}, ${accessLog.GeoIPCountry}`}</td>
                                    <td>{accessLog.AccessTag}</td>
                                  </tr>
                                ))}
                              {accessLogs.length === 0 && (
                                <tr key={0}>
                                  <td colSpan={5} className="text-center">
                                    This proposal has not been viewed by anyone
                                    else
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      )}
      {
        // when data is loaded and quote id is not valid show quote does not exists error UI
        data && data.Quote === null && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Quote with id {props.quoteId} does not exist.
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteBuilderSendProposal);
